import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput, BubbleTypeahead } from '../BubbleUI';
import { updateConfigurations } from '@go-client/lib/api/property';
import { PropertySettings } from '@go-shared/types/property';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { createPortal } from 'react-dom';
import { ObjectId } from 'bson';

interface NaveganteFormDataBase {
  config: PropertySettings;
}

type NaveganteFormData = NaveganteFormDataBase & {
  _dirty: boolean;
  _errors: Partial<NaveganteFormError>;
};

type NaveganteFormError = { [K in keyof NaveganteFormDataBase]: string };

function DEFAULT_FORM_DATA(propConfigs?: PropertySettings): NaveganteFormData {
  return {
    config: { ...propConfigs },
    _dirty: false,
    _errors: {},
  };
}

interface NaveganteFormProps {
  edit?: PropertySettings;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function NaveganteForm(props: NaveganteFormProps) {
  const { activeProperty } = useActiveProperty();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<NaveganteFormData>(DEFAULT_FORM_DATA(props.edit));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof PropertySettings>(key: T, val: PropertySettings[T]) {
    setFormData({ ...formData, _dirty: true, config: { ...formData.config, [key]: val } });
  }

  async function submitForm() {
    setSaving(true);
    try {
      await updateConfigurations(activeProperty._id, formData.config);
      await props.onFormSubmitted();
    } finally {
      setSaving(false);
    }
  }

  const additionalLinkOpts = (formData.config.naveganteAdditionalLinks ?? []).map(nal => ({
    key: nal._id,
    refKey: nal._id,
    item: nal,
  }));

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      <BubbleFormGroup label="Days to View" className="col-span-3">
        <div className="flex items-start">
          <BubbleInput
            type="checkbox"
            checked={formData.config.naveganteMobileDayCap !== undefined}
            onValue={check => {
              changeForm('naveganteMobileDayCap', check ? 3 : undefined);
            }}
            className="px-0-i pt-2 mr-2 leading-none"
          />
          <BubbleInput
            type="number"
            disabled={formData.config.naveganteMobileDayCap === undefined}
            value={formData.config.naveganteMobileDayCap}
            onValue={val => changeForm('naveganteMobileDayCap', val)}
            min={0}
            max={180}
            block
            className="flex-grow"
          />
        </div>
        <div className="text-slate italic mt-1">
          Limits the number of days ahead that guests can navigate in GO Mobile. Default is 3. Min:
          0, Max: 180. We recommend setting this to your guests' average length of stay.
        </div>
      </BubbleFormGroup>

      <BubbleFormGroup label="Additional Links" className="col-span-3">
        <BubbleTypeahead
          block
          options={additionalLinkOpts}
          placeholder="Enter link label..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={additionalLinkOpts.length > 0}
          suggestionsText="No similar links found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center space-x-3">
              <span className="text-xs font-semibold uppercase text-navy ml-3">Label</span>
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.label}
                className="w-32"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const nalIdx =
                    formData.config.naveganteAdditionalLinks?.findIndex(
                      nal => nal._id === opt.item._id
                    ) ?? -1;
                  if (nalIdx < 0) return;
                  const newAdditionalLinks = [...(formData.config.naveganteAdditionalLinks ?? [])];
                  newAdditionalLinks[nalIdx].label = val;
                  changeForm('naveganteAdditionalLinks', newAdditionalLinks);
                }}
              />
              <span className="text-xs font-semibold uppercase text-navy">Link</span>
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.url}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const nalIdx =
                    formData.config.naveganteAdditionalLinks?.findIndex(
                      nal => nal._id === opt.item._id
                    ) ?? -1;
                  if (nalIdx < 0) return;
                  const newAdditionalLinks = [...(formData.config.naveganteAdditionalLinks ?? [])];
                  newAdditionalLinks[nalIdx].url = val;
                  changeForm('naveganteAdditionalLinks', newAdditionalLinks);
                }}
              />
              <div className="flex items-center relative">
                <BubbleInput
                  type="checkbox"
                  className="mr-1"
                  inputClassName="stretched-link"
                  checked={opt.item.top}
                  onValue={chk => {
                    const nalIdx =
                      formData.config.naveganteAdditionalLinks?.findIndex(
                        nal => nal._id === opt.item._id
                      ) ?? -1;
                    if (nalIdx < 0) return;
                    const newAdditionalLinks = [
                      ...(formData.config.naveganteAdditionalLinks ?? []),
                    ];
                    newAdditionalLinks[nalIdx].top = chk;
                    changeForm('naveganteAdditionalLinks', newAdditionalLinks);
                  }}
                />
                <span>Top</span>
              </div>
              <div className="flex items-center relative">
                <BubbleInput
                  type="checkbox"
                  className="mr-1"
                  inputClassName="stretched-link"
                  checked={opt.item.isExternal}
                  onValue={chk => {
                    const nalIdx =
                      formData.config.naveganteAdditionalLinks?.findIndex(
                        nal => nal._id === opt.item._id
                      ) ?? -1;
                    if (nalIdx < 0) return;
                    const newAdditionalLinks = [
                      ...(formData.config.naveganteAdditionalLinks ?? []),
                    ];
                    newAdditionalLinks[nalIdx].isExternal = chk;
                    changeForm('naveganteAdditionalLinks', newAdditionalLinks);
                  }}
                />
                <span>External</span>
              </div>
              {opt.item.isExternal && (
                <div className="flex items-center relative">
                  <BubbleInput
                    type="checkbox"
                    className="mr-1"
                    inputClassName="stretched-link"
                    checked={opt.item.newTab}
                    onValue={chk => {
                      const nalIdx =
                        formData.config.naveganteAdditionalLinks?.findIndex(
                          nal => nal._id === opt.item._id
                        ) ?? -1;
                      if (nalIdx < 0) return;
                      const newAdditionalLinks = [
                        ...(formData.config.naveganteAdditionalLinks ?? []),
                      ];
                      newAdditionalLinks[nalIdx].newTab = chk;
                      changeForm('naveganteAdditionalLinks', newAdditionalLinks);
                    }}
                  />
                  <span>New Tab</span>
                </div>
              )}
            </div>
          )}
          onCreate={val =>
            changeForm('naveganteAdditionalLinks', [
              ...(formData.config.naveganteAdditionalLinks ?? []),
              {
                _id: new ObjectId().toHexString(),
                label: val,
                url: '',
                top: false,
                isExternal: true,
                newTab: false,
              },
            ])
          }
          onTrash={val =>
            changeForm(
              'naveganteAdditionalLinks',
              formData.config.naveganteAdditionalLinks?.filter(nal => nal._id !== val.item._id) ??
                []
            )
          }
        />
      </BubbleFormGroup>
    </div>
  );

  const formActions = (
    <div className="w-full flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
